<!-- eslint-disable vue/html-indent -->
<template>
  <section class="form-field-group">
    <!-- Terms and conditions -->
    <div class="field-row">
      <!-- Bloom - Terms and Conditions Label -->
      <input-label-wuk v-if="isBloom" :label="$t('onboarding2_question_label_terms')" class="terms-label"></input-label-wuk>

      <sword-checkbox-wuk :checked="Boolean(termsAccepted)"
                          name="terms-of-service"
                          data-testid="input_termsAccepted"
                          class="terms-of-service legal-checkbox"
                          variant="minimal"
                          :opt-value="termsAccepted"
                          :validation="validations.termsAccepted"
                          :show-error="false"
                          @change="sendTrackingEventOnChange('terms-of-service', validations.termsAccepted.$invalid,
                                                   'update:termsAccepted', String($event || ''))"
      >
        <span v-html="termsAndConditionsHandler"></span>
      </sword-checkbox-wuk>
    </div>

    <!-- GDPR Consent -->
    <div v-if="showGdprConsent" class="field-row">
      <sword-checkbox-wuk :checked="Boolean(gdprAccepted)"
                          name="gdpr-consent"
                          data-testid="input_gdprAccepted"
                          class="gdpr-consent legal-checkbox"
                          variant="minimal"
                          :opt-value="gdprAccepted"
                          :validation="validations.gdprAccepted"
                          :show-error="false"
                          @change="sendTrackingEventOnChange('gdpr-consent', validations.gdprAccepted.$invalid,
                                                   'update:gdprAccepted', String($event || ''))"
      >
        <span v-html="gdprHandler"></span>
      </sword-checkbox-wuk>
    </div>

    <!-- HIPAA consent -->
    <div v-if="showHipaaConsent" class="field-row">
      <sword-checkbox-wuk :checked="Boolean(hipaaAccepted)"
                          name="hipaa-consent"
                          data-testid="input_hipaaAccepted"
                          class="hipaa-consent legal-checkbox"
                          variant="minimal"
                          :opt-value="hipaaAccepted"
                          :show-error="false"
                          :validation="validations.hipaaAccepted"
                          @change="sendTrackingEventOnChange('hipaa-consent', validations.hipaaAccepted.$invalid,
                                                   'update:hipaaAccepted', String($event || ''))"
      >
        <span v-html="hipaaHandler"></span>
      </sword-checkbox-wuk>
    </div>

     <!-- GORILLA consent -->
     <div v-if="showGorillaConsent" class="field-row">
      <sword-checkbox-wuk :checked="Boolean(gorillaAccepted)"
                          name="gorilla-consent"
                          data-testid="input_gorillaAccepted"
                          class="gorilla-consent legal-checkbox"
                          variant="minimal"
                          :opt-value="gorillaAccepted"
                          :show-error="false"
                          @change="sendTrackingEventOnChange('gorilla-consent', null, 'update:gorillaAccepted', String($event || ''))"
      >
        <span v-html="gorillaHandler"></span>
      </sword-checkbox-wuk>
    </div>

    <!-- Minor consent -->
    <div v-if="showMinorConsent" class="field-row">
      <sword-checkbox-wuk :checked="Boolean(minorConsentAccepted)"
                          name="minor-consent"
                          data-testid="input_minorConsentAccepted"
                          class="legal-minor-consent legal-checkbox"
                          variant="minimal"
                          :opt-value="minorConsentAccepted"
                          :validation="validations.minorConsentAccepted"
                          :show-error="false"
                          @change="$emit('update:minorConsentAccepted', String($event || ''))"
      >
        <span v-html="minorConsentHandler"></span>
      </sword-checkbox-wuk>
    </div>
    <div class="field-row">
      <input-error-wuk :manual-error="checkRequired" class="fix-error-position"></input-error-wuk>
    </div>
  </section>
</template>

<script>
// EXTERNAL LIBS
import { mapGetters } from 'vuex';

// COMPONENTS
import SwordCheckboxWuk from '@ui-kit/components/inputs/checkboxes/SwordCheckbox.vue';
import InputLabelWuk from '@ui-kit/components/inputs/labels/InputLabel.vue';

// MIXINS
import tracking from '@/mixins/tracking';

// CONFIGS
import { clientStoreTypes, applicationStoreTypes } from '@/store/types';


export default {
  name: 'LegalFields',

  components: {
    SwordCheckboxWuk,
    InputLabelWuk,
  },

  mixins: [tracking],

  props: {
    termsAccepted: {
      type: [String, Boolean],
      required: true,
    },
    gdprAccepted: {
      type: [String, Boolean],
      required: true,
    },
    hipaaAccepted: {
      type: [String, Boolean],
      required: true,
    },
    gorillaAccepted: {
      type: [String, Boolean],
      required: true,
    },
    minorConsentAccepted: {
      type: [String, Boolean],
      required: true,
    },

    validations: {
      type: Object,
      default: () => ({
        termsAccepted: {},
        gdprAccepted: {},
        hipaaAccepted: {},
        gorillaAccepted: {},
        minorConsentAccepted: {},
      }),
    },

    showGdprConsent: {
      type: Boolean,
      required: true,
    },
    showHipaaConsent: {
      type: Boolean,
      required: true,
    },
    showGorillaConsent: {
      type: Boolean,
      required: true,
    },
    showMinorConsent: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      clientLegalLinks: clientStoreTypes.getters.GET_CLIENT_LEGAL_LINKS,
      clientCountry: clientStoreTypes.getters.GET_COUNTRY,
      isBloom: applicationStoreTypes.getters.IS_BLOOM,
      unit: applicationStoreTypes.getters.UNIT,
    }),
    termsAndConditionsHandler: {
      get() {
        return this.$t('onboarding_legaldocs_UK-US-Canada_1', {
          TERMS_AND_CONDITIONS_URL: this.clientLegalLinks.termsAndConditions,
          CONSENT_TO_TELEHEALTH_SERVICES_URL: this.clientLegalLinks.consentTelehealthServices,
          PRIVACY_STATEMENT_URL: this.clientLegalLinks.privacyStatement,
        });
      },
    },
    gdprHandler: {
      get() {
        return this.$t('onboarding_legaldocs_UK_2', {
          CONSENT_FOR_PROCESSING_PERSONAL_DATA_URL: this.clientLegalLinks.gdprConsent,
        });
      },
    },
    hipaaHandler: {
      get() {
        return this.$t('onboarding_legaldocs_US_HIPAA', {
          HIPAA_NOTICE_OF_PRIVACY_PRACTICES_URL: this.clientLegalLinks.hipaaConsent,
        });
      },
    },
    gorillaHandler: {
      get() {
        return this.$t('onboarding_legaldocs_US_2', {
          HEALTH_NETWORK_PARTICIPATION_CONSENT_URL: this.clientLegalLinks.gorillaConsent,
        });
      },
    },
    minorConsentHandler: {
      get() {
        return this.$t('onboarding_minor_consent_label', {
          CONSENT_URL: this.clientLegalLinks.minorConsent,
        });
      },
    },
    checkRequired() {
      const invalid = [];
      const required = Object.keys(this.validations).filter(key => this.validations[key].required === false);
      required.forEach(key => {
        if (this.validations[key].$dirty && this.validations[key].$invalid) {
          invalid.push(key);
        }
      });
      return invalid.length > 0 ? this.$t('onboarding_required_label') : '';
    },
  },
};
</script>

<style lang="scss" scoped>
  .terms-label :deep(p) {
    font-size: $font-base-12 !important;
  }

  .fix-error-position {
    /**
      the form-field-group apply a grid-row-gap of 1rem,
      the input-error-wuk should be 0.25rem.
      then I put a negative margin to simulate a 0.25rem space
      between checkbox and input-error-wuk.
    */
    margin-top: -.75rem;
    padding: 0;
  }
</style>
